/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable camelcase */
import React, { Component } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import MediaQuery from 'react-responsive';
import classNames from 'classnames';

// app modules: actions
import { openModal } from 'actions/modals';
import { configurePaywall } from 'actions/paywall';
import { showMeteredAccessBanner, hideMeteredAccessBanner } from 'actions/meteredAccess';

// app modules: components
import Button from 'components/Button';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import LayoutProps from 'components/Layout/LayoutProps';
import Link from 'components/Link';
import MembershipBenefits from 'components/MembershipBenefits';
import OfferPrice from 'components/OfferPrice';

// app modules: services
import route from 'services/route';
import { sendEvent } from 'services/analytics';
import { isApp } from 'services/environment';
import history from 'services/history';

// app modules: constants
import { BREAKPOINTS, COLOURS } from 'constants/index';
import { MODALS } from 'constants/view';

// assets
import CssMeteredAccess from './MeteredAccess.scss';

export class MeteredAccessBannerComponent extends Component {
  static propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    accessPolicy: PropTypes.object,
    buildPlatform: PropTypes.string,
    canView: PropTypes.bool,
    coupon: PropTypes.string,
    defaultSubscriptionPlanChoice: PropTypes.string,
    dimensions: LayoutProps.dimensions.isRequired,
    extended: PropTypes.bool,
    hasQuota: PropTypes.bool,
    hideMeteredAccessBanner: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    isFirstClickFreeAccess: PropTypes.bool,
    modal: PropTypes.bool,
    openModal: PropTypes.func.isRequired,
    configurePaywall: PropTypes.func.isRequired,
    permittedItems: PropTypes.arrayOf(PropTypes.string),
    quota: PropTypes.number,
    quotaPeriod: PropTypes.string,
    showMeteredAccessBanner: PropTypes.func.isRequired,
    used: PropTypes.number,
    visible: PropTypes.bool,
    subscription: PropTypes.shape({ is_valid: PropTypes.bool }),
    canUseIntroOffer: PropTypes.bool,
    withPromo: PropTypes.bool,
  };

  static defaultProps = {
    accessPolicy: null,
    buildPlatform: 'web',
    canUseIntroOffer: true,
    canView: false,
    coupon: null,
    defaultSubscriptionPlanChoice: null,
    extended: false,
    hasQuota: false,
    isFirstClickFreeAccess: false,
    modal: false,
    permittedItems: [],
    quota: 0,
    quotaPeriod: '',
    subscription: null,
    used: 0,
    visible: true,
    withPromo: false,
  };

  constructor(props) {
    super(props);
    // Needed for the server side render to correctly set the paywall state.
    this.checkAccess();

    this.bound = {
      handleTrialButtonClick: this.handleTrialButtonClick.bind(this),
    };

    this.state = {
      ready: false,
    };
  }

  componentDidMount() {
    const { extended, withPromo, isFirstClickFreeAccess, visible } = this.props;
    this.checkAccess();
    this.setIntercomVisibility();
    if (visible && isFirstClickFreeAccess) {
      sendEvent({
        event: 'MeteredAccess.FirstClickFree',
      });
    }
    if (visible && extended && withPromo) {
      sendEvent({
        event: 'MeteredAccess.PromoDisplayed',
      });
    }
  }

  componentDidUpdate(prevProps) {
    // If the content or subscription validity changes check access permissions.
    if (
      prevProps.id !== this.props.id ||
      prevProps.subscription?.is_valid !== this.props.subscription?.is_valid
    ) {
      this.checkAccess();
    }
  }

  componentWillUnmount() {
    const { accessPolicy } = this.props;
    this.props.hideMeteredAccessBanner({ accessPolicy });
  }

  // eslint-disable-next-line class-methods-use-this
  setIntercomVisibility() {
    if (window.Intercom && isApp()) {
      window.Intercom('update', {
        hide_default_launcher: true,
      });
    }
  }

  getPeriodText() {
    const { quotaPeriod } = this.props;
    if (quotaPeriod) {
      switch (quotaPeriod) {
        case 'day':
          return 'today';
        default:
          return `this ${quotaPeriod}`;
      }
    }
    return '';
  }

  handleExpandCollapseClick = () => {
    const { modal, extended, accessPolicy } = this.props;
    if (modal) {
      return;
    }
    this.props.showMeteredAccessBanner({ accessPolicy, modal: false, extended: !extended });
  };

  handleTrialButtonClick() {
    const { canUseIntroOffer } = this.props;
    if (isApp()) {
      import('components/InAppPaymentInfo/InAppPaymentInfo.jsx' /* webpackChunkName: "in-app-payment", webpackMode: "lazy" */).then(
        module => {
          this.props.openModal({
            name: MODALS.IN_APP_PAYMENT,
            content: module.default,
          });
        },
      );
    } else {
      history.push('/subscribe', {
        returnTo: history.location,
        heading: 'Premium Membership',
        withSteps: false,
        introText: 'For unlimited access to ckbk try Premium Membership',
        canUseIntroOffer,
      });
    }
  }

  handlePromoClick = () => {
    sendEvent({
      event: 'MeteredAccess.PromoClicked',
    });
  };

  openMeteredPaywall() {
    this.props.configurePaywall({
      heading: "You've read all your free recipes this month",
      bodyContent: (
        <p className="metered-access__emphasis">
          <strong>To keep reading try Premium Membership</strong>
        </p>
      ),
    });
  }

  openPremiumContentPaywall() {
    this.props.configurePaywall({
      heading: 'Premium Membership Required',
      bodyContent: (
        <p>
          <strong>For unlimited access to ckbk try Premium Membership</strong>
        </p>
      ),
    });
  }

  checkAccess() {
    // This checks whether to display the modal dialog paywall.
    // For first click free we take a different CSS based approach (see renderFirstClickFree).
    const { canView, hasQuota, used, quota, isFirstClickFreeAccess } = this.props;
    if (!isFirstClickFreeAccess && !canView) {
      // Permission Denied
      if (hasQuota && used >= quota) {
        // Denied due to running out of free quota
        this.openMeteredPaywall();
      } else {
        // Denied because this content is not accessible to free users.
        this.openPremiumContentPaywall();
      }
    }
  }

  renderLoggedInUser() {
    const {
      buildPlatform,
      canUseIntroOffer,
      coupon,
      defaultSubscriptionPlanChoice,
      dimensions,
      hasQuota,
      permittedItems,
      quota,
      subscription,
      used,
    } = this.props;
    if ((subscription && subscription.is_valid) || !hasQuota) {
      return null;
    }
    const remaining = Math.max(0, quota - used);

    const periodText = this.getPeriodText();

    const statusIndicators = permittedItems.slice(0, quota).map((item, idx) => (
      <li className="metered-access__item  metered-access__item--active" key={item}>
        <Link to={route.content(item)}>
          <Icon fill="#f8f8f8" name="circletick-solid" height="20" width="20" /> {idx + 1}
        </Link>
      </li>
    ));

    for (let idx = statusIndicators.length; idx < quota; idx += 1)
      statusIndicators.push(
        <li className="metered-access__item" key={`metered_access_${this.salt}_${idx}`}>
          <Icon fill="#f8f8f8" name="circletick-outline" height="20" width="20" /> {idx + 1}
        </li>,
      );

    const free = dimensions.body > BREAKPOINTS.PHONE_VERTICAL ? 'free ' : '';

    const message =
      remaining > 0
        ? `You have ${remaining} ${free}${
            remaining === 1 ? 'recipe' : 'recipes'
          } left ${periodText}`
        : `This is your last ${free}recipe ${periodText}`;

    return (
      <div
        className={classNames('metered-access__wrapper', {
          'metered-access__wrapper--above-tabbar': buildPlatform === 'app',
        })}
      >
        {coupon && (
          <MediaQuery maxWidth={BREAKPOINTS.TABLET_VERTICAL}>
            <OfferPrice className="metered-access__offer" condensed />
          </MediaQuery>
        )}
        <div className="metered-access__contents metered-access__contents--logged-in">
          <ul className="metered-access__items">{statusIndicators}</ul>
          <div>
            <p className="metered-access__emphasis">{message}</p>
            <MediaQuery minWidth={BREAKPOINTS.TABLET_VERTICAL}>
              <p>Upgrade to ckbk Premium Membership for unlimited access</p>
              <OfferPrice />
            </MediaQuery>
          </div>

          <div>
            <p className="metered-access__emphasis">
              <Button
                themeName="energized"
                size="extra-large"
                className="metered-access__cta"
                onClick={this.bound.handleTrialButtonClick}
              >
                <MediaQuery minWidth={BREAKPOINTS.TABLET_VERTICAL}>
                  {matches => {
                    if (matches && canUseIntroOffer) {
                      return defaultSubscriptionPlanChoice === 'annual' ? (
                        'Try ckbk Premium Membership'
                      ) : (
                        <span>
                          Start 14 day <span className="metered-access__nobr">free trial</span>
                        </span>
                      );
                    }
                    return 'Upgrade to Premium';
                  }}
                </MediaQuery>
              </Button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // eslint-disable-next-line class-methods-use-this
  renderPromoLink(number) {
    return (
      <a
        className={`metered-access__promo-link${number}`}
        href="https://www.neff-home.com/uk/discover/kitchen-inspiration/inspiration-hub?cid=nf_reu_br_CKBK_20:soci::cpc"
        target="_blank"
        rel="noopener noreferrer"
        onClick={this.handlePromoClick}
      />
    );
  }

  renderFirstClickFree() {
    const { canView, hasQuota, used, quota, extended, withPromo } = this.props;
    const { ready } = this.state;
    const periodText = this.getPeriodText();
    let message;
    if (!canView) {
      message = 'Join ckbk for free to view this recipe';
    } else if (hasQuota && quota === used) {
      message = `This is your last recipe ${periodText}. Join ckbk for free to get more`;
    } else {
      message = 'Read more recipes when you join ckbk for free';
    }
    const closeButton = (
      <button
        type="button"
        className={classNames('metered-access__close', {
          'metered-access__close--flipped': !extended,
        })}
        onClick={this.handleExpandCollapseClick}
      >
        <Icon fill={COLOURS.ENERGIZED} name="caret-down" height="22" width="22" mr={5} />
      </button>
    );

    const joinButton = (
      <Button
        themeName="energized"
        size="extra-large"
        className="metered-access__cta-compact"
        onClick={() => {
          history.push('/signup', { returnTo: history.location });
        }}
      >
        Join ckbk
      </Button>
    );

    return (
      <div
        className={classNames('metered-access__wrapper', {
          'metered-access__wrapper--extended': extended,
          'metered-access__wrapper--promo': withPromo && extended,
          'metered-access__wrapper--hidden': !ready,
        })}
      >
        {withPromo && extended ? (
          <React.Fragment>
            {this.renderPromoLink(1)}
            {this.renderPromoLink(2)}
          </React.Fragment>
        ) : null}
        <div
          className={classNames('metered-access__contents', {
            'metered-access__contents--extended': extended,
          })}
        >
          <div
            className={classNames('metered-access__body', {
              'metered-access__body--extended': extended,
            })}
          >
            {extended ? (
              <Heading className="metered-access__heading">{message}</Heading>
            ) : (
              <p className="metered-access__message">{message}</p>
            )}
            {extended ? (
              <React.Fragment>
                <MembershipBenefits
                  compact
                  premium={false}
                  itemClassName="metered-access__benefit-item"
                  className="metered-access__benefits margin-bottom-5"
                  onLoad={() => this.setState({ ready: true })}
                />
              </React.Fragment>
            ) : null}
            <div
              className={classNames('metered-access__actions', {
                'metered-access__actions--extended': extended,
              })}
            >
              <div>{joinButton}</div>
              <div className="metered-access__login">
                Already a member?{' '}
                <button
                  onClick={() => history.push('/login', { returnTo: history.location })}
                  className="link link--energized metered-access__nobr"
                >
                  Log in
                </button>
              </div>
            </div>
          </div>
          {closeButton}
        </div>
      </div>
    );
  }

  render() {
    const { isFirstClickFreeAccess, visible } = this.props;
    if (visible) {
      if (isFirstClickFreeAccess) {
        return this.renderFirstClickFree();
      }
      return this.renderLoggedInUser();
    }
    return null;
  }
}

const mapStateToProps = state => ({
  coupon: state.paywall.coupon,
  dimensions: state.dimensions,
  visible: state.meteredAccess.visible,
  modal: state.meteredAccess.modal,
  extended: state.meteredAccess.extended,
  buildPlatform: state.config.platform,
  subscription: state.user.subscription,
  canUseIntroOffer: !state.user.subscription,
  defaultSubscriptionPlanChoice: state.config.paywall.defaultSubscriptionPlanChoice,
});

const mapDispatchToProps = {
  openModal,
  configurePaywall,
  showMeteredAccessBanner,
  hideMeteredAccessBanner,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(CssMeteredAccess)(MeteredAccessBannerComponent));
